// Which numbers fit a player's position (DIRECTION U29,
// PLAN-2026-10-26-numbers-that-fit-the-position §9): the filters a list of
// players offers, the columns each one reads by, how each column is worked
// out from a player's lines, and whether the sport's usage describes him.
// ONE COPY for the pool, the card, My Team's tables, Compare, the simulator
// and a client's labels, so no two can disagree about what a position is
// shown by.
//
// A POSITION'S OWN COLUMNS are the sport's stat sheet for it
// (`DisplayRules.statSheet`): the stats it earns its points by and the
// volume behind them, each at the least detail level it shows at, whatever a
// player's numbers — a quarterback's interceptions at 0 are a figure, not a
// gap. A chip of several positions (a Flex) reads the union of their sheets.
// Then, at Everything, every other stat the league scores in the position's
// kinds of play (`ScoringLayout.sections[].main`) that no column reads. A
// sport with no sheet reads its group's box score as before, its casual
// columns at Simple.
//
// A GAME'S AVERAGE, OR THE GAMES' TOTAL (B-520): a table read a game
// (`perGame`) divides each count by the games it is over, since a manager
// reads a list for what to expect of a player's next game; the card's log
// and its totals read the games' totals.
//
// NO SPORT IS NAMED. Positions, slots, stats and kinds of play are read from
// the definition handed in.

import type {
  ScoringConfigLike,
  SheetFigure,
  SheetLevel,
  SportDefinition,
  StatSheetColumn,
} from '@fsp/sports-core';
import type { PositionFilterView, StatColumnView } from '../api/views';

export type NumbersDefinition = Pick<
  SportDefinition,
  'positions' | 'slots' | 'stats' | 'display' | 'scoringLayout' | 'usage'
>;

/** A table's columns, and how each is worked out from a player's lines. */
export interface PositionTable {
  /** The positions it is for, in the sport's order. */
  positions: string[];
  columns: StatColumnView[];
  /** Each column's figure, by its key (`sheetValues`). */
  figures: ReadonlyMap<string, SheetFigure>;
  /** Its counts a game's average over the games, not their total (B-520). */
  perGame: boolean;
}

export interface PositionNumbers {
  /** How a position reads on screen: its own starting slot's name, or its id (`DST` reads *DEF*). */
  label(position: string): string;
  /** The filters a list offers, from the league's starting slots (§3.1). */
  filters(starting: readonly { id: string; eligible: readonly string[] }[]): PositionFilterView[];
  /**
   * The table for these positions: their sheets' columns, and the stats the
   * league scores beyond them (§9.2); its counts a game's average with
   * `perGame` (B-520).
   */
  table(
    positions: readonly string[],
    scores?: (stat: string) => boolean,
    options?: { perGame?: boolean },
  ): PositionTable;
  /** Whether the sport's usage describes a position: its share stat is one of his kinds (§3.4). */
  usageDescribes(position: string): boolean;
}

const LEVELS: readonly SheetLevel[] = ['simple', 'standard', 'everything'];
const lower = (a: SheetLevel, b: SheetLevel): SheetLevel =>
  LEVELS.indexOf(a) <= LEVELS.indexOf(b) ? a : b;

/** The key a made-of column's attempts are under, beside its made (`StatColumnView.attempts`). */
export const attemptsKey = (key: string): string => `${key}_att`;

/** The stats a figure reads. */
function statsOf(f: SheetFigure): readonly string[] {
  if ('stat' in f) return [f.stat];
  if ('sum' in f) return f.sum;
  if ('rate' in f) return [...f.rate, ...f.per];
  if ('perGame' in f) return [f.perGame];
  if ('made' in f) return [...f.made, ...f.missed];
  return [f.share];
}

const roundTo = (n: number, places: number): number => {
  const f = 10 ** places;
  return Math.round(n * f) / f;
};

/**
 * A row's numbers in a table's columns over some games, one line a game (a
 * week's merged line will do). A COUNT A LINE DOES NOT HAVE IS 0 once there
 * is a line — no interception is no interceptions — EXCEPT A TIERED STAT
 * (points allowed): one no line reported is left out, since a 0 there reads
 * as a shutout, and a figure a game is over the games that reported it. A
 * rate over nothing (yards a carry with no carries) is left out, not 0. A
 * made-of column holds the made under its key and the tries under
 * `attemptsKey`. A share is the host's to give (`share`); without, left out.
 * A table read a game (`perGame`) divides each count, and a made-of
 * column's made and tries, by the games, as a figure a game is. No lines,
 * no numbers.
 */
export function sheetValues(
  table: {
    columns: readonly { key: string; decimals: number }[];
    figures: PositionTable['figures'];
    perGame?: boolean;
  },
  lines: readonly Readonly<Record<string, number>>[],
  tiered: ReadonlySet<string>,
  share?: (stat: string) => number | null,
): Record<string, number> {
  const out: Record<string, number> = {};
  if (lines.length === 0) return out;
  const sum = (keys: readonly string[]) =>
    keys.reduce((s, k) => s + lines.reduce((t, l) => t + (l[k] ?? 0), 0), 0);
  const had = (k: string) => lines.filter((l) => l[k] !== undefined).length;
  const unreported = (keys: readonly string[]) => keys.every((k) => tiered.has(k) && had(k) === 0);
  // The games a count is over: those that reported a tiered stat, every
  // line's for the rest; one, for a table read as the games' total.
  const over = (keys: readonly string[]) =>
    !table.perGame
      ? 1
      : keys.every((k) => tiered.has(k))
        ? Math.max(1, ...keys.map(had))
        : lines.length;
  for (const c of table.columns) {
    const f = table.figures.get(c.key);
    if (f === undefined) continue;
    const places = Math.max(c.decimals, 1);
    if ('stat' in f || 'sum' in f) {
      const keys = 'stat' in f ? [f.stat] : f.sum;
      if (!unreported(keys)) out[c.key] = roundTo(sum(keys) / over(keys), places);
    } else if ('rate' in f) {
      const per = sum(f.per);
      if (per !== 0) out[c.key] = roundTo((sum(f.rate) / per) * (f.percent ? 100 : 1), places);
    } else if ('perGame' in f) {
      const games = tiered.has(f.perGame) ? had(f.perGame) : lines.length;
      if (games > 0) out[c.key] = roundTo(sum([f.perGame]) / games, places);
    } else if ('made' in f) {
      const made = sum(f.made);
      const games = over([...f.made, ...f.missed]);
      out[c.key] = roundTo(made / games, places);
      out[attemptsKey(c.key)] = roundTo((made + sum(f.missed)) / games, places);
    } else {
      const v = share?.(f.share) ?? null;
      if (v !== null) out[c.key] = roundTo(v * 100, places);
    }
  }
  return out;
}

/** Whether a league scores a stat for any of these positions: a unit, a bonus or a tier on it. */
export function scoresFor(
  config: Pick<ScoringConfigLike, 'units' | 'bonuses' | 'tiers'>,
  positions: readonly string[],
): (stat: string) => boolean {
  const scored = new Set<string>();
  for (const r of [...config.units, ...config.bonuses, ...config.tiers]) {
    if (r.positions === null || r.positions.some((p) => positions.includes(p))) scored.add(r.stat);
  }
  return (stat) => scored.has(stat);
}

/** "RB, WR or TE". */
function either(words: readonly string[]): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} or ${words.at(-1)}`;
}

/**
 * Several sheets as one (a Flex's): each column once, at the lowest level
 * any sheet shows it, a new one placed after the column it follows in its
 * own sheet — a receiver's yards a catch after the back's receiving yards.
 */
function unionOf(sheets: readonly (readonly StatSheetColumn[])[]): StatSheetColumn[] {
  const out: StatSheetColumn[] = [];
  for (const sheet of sheets) {
    let after = -1;
    for (const c of sheet) {
      const at = out.findIndex((o) => o.key === c.key);
      if (at >= 0) {
        out[at] = { ...out[at]!, level: lower(out[at]!.level, c.level) };
        after = at;
        continue;
      }
      out.splice(after + 1, 0, c);
      after += 1;
    }
  }
  return out;
}

export function positionNumbersFor(definition: NumbersDefinition): PositionNumbers {
  const order = [...definition.positions].sort((a, b) => a.sortOrder - b.sortOrder);
  const rank = new Map(order.map((p, i) => [p.id, i]));
  const positionDef = new Map(order.map((p) => [p.id, p]));
  const statDef = new Map(definition.stats.map((s) => [s.key, s]));
  const layout = definition.scoringLayout;
  const kindOfStat = new Map<string, string>();
  for (const g of layout?.groups ?? []) for (const s of g.stats) kindOfStat.set(s, g.id);
  const sections = new Map((layout?.sections ?? []).map((s) => [s.position, s]));
  const slotOrder = new Map(definition.slots.map((s) => [s.id, s.sortOrder]));
  const slotName = new Map(definition.slots.map((s) => [s.id, s.name]));
  const inOrder = (ids: Iterable<string>) =>
    [...new Set(ids)].sort((a, b) => (rank.get(a) ?? 99) - (rank.get(b) ?? 99));

  const label = (position: string): string =>
    definition.slots.find(
      (s) => s.kind === 'starter' && s.eligible.length === 1 && s.eligible[0] === position,
    )?.name ?? position;

  /** A position's own kinds of play, or null where the sport lays none out. */
  const ownKinds = (position: string): readonly string[] | null =>
    sections.get(position)?.main ?? null;

  const usageDescribes = (position: string): boolean => {
    const share = definition.usage?.share;
    if (share === undefined) return false;
    const kind = kindOfStat.get(share) ?? null;
    const own = ownKinds(position);
    // With no layout to say which kinds are his, the sport's usage is
    // everybody's, as it was.
    return own === null ? true : kind !== null && own.includes(kind);
  };

  const filters = (
    starting: readonly { id: string; eligible: readonly string[] }[],
  ): PositionFilterView[] => {
    const anyone = starting.some((s) => s.eligible.length === 0);
    const startable = anyone
      ? order.map((p) => p.id)
      : order.map((p) => p.id).filter((p) => starting.some((s) => s.eligible.includes(p)));
    // ONE CHIP PER SET A SLOT TAKES, named by the first slot to take it:
    // a Flex and a W/R/T over the same three are one choice.
    const multis: { id: string; name: string; positions: string[] }[] = [];
    const seen = new Set<string>();
    for (const s of [...starting].sort(
      (a, b) => (slotOrder.get(a.id) ?? 99) - (slotOrder.get(b.id) ?? 99),
    )) {
      if (s.eligible.length < 2) continue;
      const positions = inOrder(s.eligible.filter((p) => rank.has(p)));
      const key = positions.join('|');
      if (positions.length < 2 || seen.has(key)) continue;
      seen.add(key);
      multis.push({ id: s.id, name: slotName.get(s.id) ?? s.id, positions });
    }
    const out: PositionFilterView[] = [];
    for (const p of startable) {
      out.push({ id: p, label: label(p), name: positionDef.get(p)?.name ?? p, positions: [p] });
      // A SET AFTER THE LAST OF ITS POSITIONS: Flex after TE.
      for (const m of multis) {
        if (m.positions.at(-1) !== p) continue;
        out.push({
          id: m.id,
          label: m.name,
          name: `${m.name}: ${either(m.positions.map(label))}`,
          positions: m.positions,
        });
      }
    }
    return out;
  };

  /** A sport with no sheet for a position: its group's box score, the casual ones at Simple. */
  const boxScore = (groups: readonly string[]): StatSheetColumn[] => {
    const casual = new Set(groups.flatMap((g) => definition.display.tableColumns(g, 'casual')));
    const keys = [...new Set(groups.flatMap((g) => definition.display.gameLogColumns(g)))];
    return keys.map((key) => ({
      key,
      level: casual.has(key) ? 'simple' : 'standard',
      figure: { stat: key },
    }));
  };

  const table = (
    positions: readonly string[],
    scores?: (stat: string) => boolean,
    options: { perGame?: boolean } = {},
  ): PositionTable => {
    const perGame = options.perGame === true;
    const ids = inOrder(positions.filter((p) => rank.has(p)));
    const sheets = ids.map((p) => definition.display.statSheet?.(p) ?? null);
    const sheet =
      ids.length > 0 && sheets.every((s) => s !== null)
        ? unionOf(sheets as StatSheetColumn[][])
        : boxScore([...new Set(ids.map((p) => positionDef.get(p)!.group))]);
    // EVERYTHING ELSE THE LEAGUE SCORES in his kinds of play, after the
    // sheet, in the layout's order: a column for what earns points.
    const covered = new Set(sheet.flatMap((c) => statsOf(c.figure)));
    const own = new Set(ids.flatMap((p) => ownKinds(p) ?? []));
    const extra: StatSheetColumn[] = [];
    if (scores !== undefined) {
      for (const g of layout?.groups ?? []) {
        if (!own.has(g.id)) continue;
        for (const key of g.stats)
          if (!covered.has(key) && scores(key))
            extra.push({ key, level: 'everything', figure: { stat: key } });
      }
    }
    const columns: StatColumnView[] = [];
    const figures = new Map<string, SheetFigure>();
    for (const c of [...sheet, ...extra]) {
      const def = 'stat' in c.figure ? statDef.get(c.figure.stat) : undefined;
      if ('stat' in c.figure && def === undefined) continue;
      const label = c.label ?? def?.shortName ?? c.key;
      const rate = 'rate' in c.figure || 'perGame' in c.figure || 'share' in c.figure;
      const name = c.name ?? def?.name ?? label;
      // A COUNT READ A GAME says so, to a tenth: 0.6 touchdowns a game.
      const divided = perGame && !rate;
      const decimals = c.decimals ?? def?.decimals ?? (rate ? 1 : 0);
      columns.push({
        key: c.key,
        label,
        name: divided ? `${name.replace(/\.$/, '')}, a game.` : name,
        casual: c.level === 'simple',
        level: c.level,
        decimals: divided ? Math.max(decimals, 1) : decimals,
        attempts: 'made' in c.figure ? attemptsKey(c.key) : null,
        perGame: divided,
      });
      figures.set(c.key, c.figure);
    }
    return { positions: ids, columns, figures, perGame };
  };

  return { label, filters, table, usageDescribes };
}
